import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, KeyRound, BookOpen, RefreshCw,
  ClipboardCheck, BarChart3, Bell, LogOut, Stethoscope,
} from "lucide-react";

const NAV = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/admin", key: "" },
  { label: "Nurses", icon: Users, path: "/admin/nurses", key: "nurses" },
  { label: "Access Requests", icon: KeyRound, path: "/admin/access-requests", key: "access-requests", badge: "pending_access" },
  { label: "Training Modules", icon: BookOpen, path: "/admin/modules", key: "modules" },
  { label: "Reattempt Requests", icon: RefreshCw, path: "/admin/reattempts", key: "reattempts", badge: "reattempts" },
  { label: "Grading", icon: ClipboardCheck, path: "/admin/grading", key: "grading", badge: "needs_grading" },
  { label: "Reports", icon: BarChart3, path: "/admin/reports", key: "reports" },
  { label: "Notifications", icon: Bell, path: "/admin/notifications", key: "notifications" },
];

export default function AdminShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [kpis, setKpis] = useState({});
  const active = location.pathname.replace("/admin", "").replace("/", "").split("/")[0];

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setKpis(r.data.kpis)).catch(() => {});
  }, [location.pathname]);

  const doLogout = async () => { await logout(); navigate("/admin/login", { replace: true }); };

  return (
    <div className="flex min-h-screen bg-[#fcf9f8]">
      <aside className="w-[280px] fixed h-screen border-r border-[#E5E7EB] bg-white flex flex-col z-30" data-testid="admin-sidebar">
        <div className="px-6 py-5 border-b border-[#E5E7EB] flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#003d9b] flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-bold text-[#1c1b1b] leading-tight">MITRA Admin</p>
            <p className="text-xs text-[#434654]">Training Console</p>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => {
            const Icon = n.icon;
            const isActive = active === n.key;
            const badge = n.badge ? kpis[n.badge] : 0;
            return (
              <button
                key={n.label}
                data-testid={`admin-nav-${n.key || "dashboard"}`}
                onClick={() => navigate(n.path)}
                className={`w-full flex items-center gap-3 px-3 h-11 rounded-lg text-sm font-semibold transition-colors ${
                  isActive ? "bg-[#003d9b] text-white" : "text-[#434654] hover:bg-gray-100"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1 text-left">{n.label}</span>
                {badge > 0 && (
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${isActive ? "bg-white text-[#003d9b]" : "bg-[#ba1a1a] text-white"}`}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <button onClick={doLogout} data-testid="admin-logout-btn"
          className="m-3 flex items-center gap-3 px-3 h-11 rounded-lg text-sm font-semibold text-[#ba1a1a] hover:bg-red-50 transition-colors">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </aside>
      <main className="ml-[280px] flex-1 p-8 min-w-0">
        <div className="mitra-fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
